import { Box } from '@razorpay/blade/components';
import api from 'api';
import { routePaths } from 'components/Routes/data';
import { salaryComponentsQueries } from 'components/SalaryComponents/constants';
import { useComponentParams } from 'components/SalaryComponents/hooks';
import { NonPayableBenefitsComponentType } from 'components/SalaryComponents/types';
import { WizardError, WizardHeader, WizardWrapper } from 'components/WizardViews/components';
import WizardPathToStepController from 'components/WizardViews/components/WizardPathToStepController';
import { WizardInfoProvider } from 'components/WizardViews/providers';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Redirect, useHistory } from 'react-router-dom';
import { NonPayableBenefitsFormData } from '../types';
import { convertSalaryComponentToNonPayableBenefitsFormData } from '../utils';
import { editNonPayableBenefitsRouteToStepMap } from '../constants';
import GeneralDetailsStep from './GeneralDetailsStep';
import { FormProvider } from 'forms';
import { FullScreenLoader } from 'components/ui/FullScreenLoader';

const EditNonPayableBenefitsForm = () => {
  const { componentId } = useComponentParams();
  const history = useHistory();
  const [formData, setFormData] = useState<NonPayableBenefitsFormData | null>(null);

  const { isFetching, error, refetch } = useQuery({
    queryKey: [salaryComponentsQueries.getSalaryComponentById, componentId],
    queryFn: () =>
      api.salaryComponents.queries.getSalaryComponent({
        id: componentId!,
        category: 'EARNINGS_CATEGORY',
      }),
    enabled: !!componentId,
    onSuccess(data) {
      const convertedFormData = convertSalaryComponentToNonPayableBenefitsFormData(
        data.payrollGetSalaryComponent.data.salaryComponent as NonPayableBenefitsComponentType,
      );
      setFormData(convertedFormData);
    },
  });

  // Redirect to list view if no component ID is present
  if (!componentId) {
    return <Redirect to={routePaths.salaryComponents.nonPayableBenefitsListView} />;
  }

  const handleCloseClick = () =>
    history.push(routePaths.salaryComponents.nonPayableBenefitsListView);

  return isFetching ? (
    <FullScreenLoader accessibilityLabel="loading non-payable benefits components" />
  ) : (
    <WizardInfoProvider initialStep={0} totalSteps={1}>
      <FormProvider>
        <WizardPathToStepController routeStepMap={editNonPayableBenefitsRouteToStepMap} />
        <WizardWrapper>
          <WizardHeader
            heading="Edit"
            subHeading="Non-Payable Benefit Component"
            onCloseClick={handleCloseClick}
          />
          <Box flex={1} overflowY="auto" height="100%">
            <WizardError
              onRetry={() => {
                refetch();
              }}
              error={error}>
              <GeneralDetailsStep
                formData={formData}
                setFormData={setFormData}
                isError={!!error}
                onRefresh={refetch}
                isLoading={isFetching}
              />
            </WizardError>
          </Box>
        </WizardWrapper>
      </FormProvider>
    </WizardInfoProvider>
  );
};

export default EditNonPayableBenefitsForm;
